import { Component, ElementRef, Inject, Input, ViewChild } from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialog,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CancelConfirmationComponent } from 'src/app/components/cancel-confirmation/cancel-confirmation.component';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { Mode } from 'src/app/models/model';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import {
	DeviceControl,
	emptyDeviceAssignment,
	emptyDeviceControl,
} from 'src/app/models/data/deviceControl';
import { ApiService } from 'src/app/services/api.services';
import { DeviceAssignments } from '../../shared/assign-device-controls/assign-device-controls.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { DayOfWeekToggleComponent } from '../../../components/day-of-week-toggle/day-of-week-toggle.component';
import { MilitaryTimePickerComponent } from '../../../components/military-time-picker/military-time-picker.component';
import { MatDivider } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { InfoboxComponent } from '../../../components/infobox/infobox.component';

/**
 * NOTE FOR PDQ (LOCK SET) DEVICE CAPABILITIES
 *
 * US14633: for PDQ devices or device groups ONLY containing PDQ devices, we hide all most other device capabilities
 * relating to lock sets and handle setting all other device capability functionality on the back end.
 * Currently, we enable all other device capabilities by default, with the exception of generating access codes, by default.
 */
@Component({
	selector: 'app-device-control-details-common',
	templateUrl: './device-control-details-common.component.html',
	styleUrls: ['./device-control-details-common.component.scss'],
	imports: [
		MatCheckbox,
		FormsModule,
		NgClass,
		DayOfWeekToggleComponent,
		MilitaryTimePickerComponent,
		MatDivider,
		MatIcon,
		MatTooltip,
		InfoboxComponent,
	],
})
export class DeviceControlDetailsCommonComponent {
	@ViewChild('checkbox247Access') public checkbox247Access!: ElementRef;

	private readonly deviceControlId: string;
	readonly control: DeviceControl;
	readonly deviceAssignments: DeviceAssignments;
	readonly edit = Mode.edit;
	readonly create = Mode.create;

	@Input() edits: DeviceControl;
	@Input() assignmentType: string = '';
	@Input() deviceAssignmentEdits: DeviceAssignments;
	@Input() isHaspCodeGenFeatureEnabled: boolean = false;
	@Input() isPDQOnly: boolean = false;
	@Input() isSentriVaultDevice: boolean = false;
	/**
	 * True for Senturian/PDQ-only, SentriVault-only, or mixed groups of only
	 * those types (matches backend special-grant access handling).
	 */
	@Input() isSpecialGrantAccessOnly: boolean = false;
	/** True for a Gen3 wall-mount device or a group that contains only those. */
	@Input() isGen3WallMountOnly: boolean = false;
	@Input() minStartTime: string = '00:00:00';
	@Input() maxEndTime: string = '00:00:00';

	/** Prefer explicit special-grant flag; fall back to legacy PDQ/SentriVault inputs. */
	get isSpecialGrantAccessMode(): boolean {
		return (
			this.isSpecialGrantAccessOnly ||
			this.isPDQOnly ||
			this.isSentriVaultDevice
		);
	}

	get showSpecialCompanyCapabilitySection(): boolean {
		return this.isSpecialGrantAccessMode && this.assignmentType === 'company';
	}

	get showStandardDeviceCapabilities(): boolean {
		return !this.isSpecialGrantAccessMode;
	}

	/** Hasp codes are not supported on Gen3-only wall mounts (Company Access). */
	get showGenerateHaspCodeCapability(): boolean {
		return (
			this.isHaspCodeGenFeatureEnabled &&
			this.assignmentType === 'company' &&
			!this.isGen3WallMountOnly
		);
	}

	get showAjaxDeviceCapabilities(): boolean {
		return (
			this.hasAtLeastOneAJAXDevice &&
			!this.edits.permissions.canArmDisarmAjaxAlarm.disabled
		);
	}

	get showSentriVaultCapabilities(): boolean {
		return this.isSentriVaultDevice;
	}

	get showDeviceCapabilitiesSection(): boolean {
		return (
			this.showStandardDeviceCapabilities ||
			this.showAjaxDeviceCapabilities ||
			this.showSpecialCompanyCapabilitySection
		);
	}

	/** Device Access is on if either legacy open-door or release-hasp flag is set. */
	get isDeviceAccessChecked(): boolean {
		return (
			!!this.edits.permissions.canOpenKeyDoor.value ||
			!!this.edits.permissions.canReleaseHasp.value
		);
	}

	get isDeviceAccessDisabled(): boolean {
		return (
			!!this.edits.permissions.canOpenKeyDoor.disabled &&
			!!this.edits.permissions.canReleaseHasp.disabled
		);
	}

	/** AJAX gated on Device Access only when the Device Access checkbox is shown. */
	get isArmDisarmUiDisabled(): boolean {
		return (
			!!this.edits.permissions.canArmDisarmAjaxAlarm.disabled ||
			(this.showStandardDeviceCapabilities && !this.isDeviceAccessChecked)
		);
	}

	get isAlarmOverrideUiDisabled(): boolean {
		return (
			!!this.edits.permissions.canOverrideAjaxAlarm.disabled ||
			!this.edits.permissions.canArmDisarmAjaxAlarm.value ||
			(this.showStandardDeviceCapabilities && !this.isDeviceAccessChecked)
		);
	}

	getEffectiveMinTime(): string {
		const add30Minutes = (timeStr: string) => {
			const [hours, minutes] = timeStr.split(':').map(Number);
			const timeInMinutes = hours * 60 + minutes;
			const adjustedMinutes = timeInMinutes + 30;
			const adjustedHours = Math.floor(adjustedMinutes / 60);
			const remainingMinutes = adjustedMinutes % 60;
			return `${String(adjustedHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}:00`;
		};
		const baseMinTime =
			this.edits.startTime && this.edits.startTime > this.minStartTime
				? this.edits.startTime
				: this.minStartTime;
		// Add 30 minutes to the min time
		const adjustedMinTime = add30Minutes(baseMinTime);
		return adjustedMinTime;
	}

	getEffectiveMaxTime(): string {
		if (
			this.edits.endTime &&
			this.edits.endTime !== '00:00:00' &&
			this.edits.endTime < this.maxEndTime
		) {
			return this.edits.endTime;
		}
		return this.maxEndTime;
	}

	// If at least one device has AJAX, the below is true
	@Input() hasAtLeastOneAJAXDevice: boolean = false;
	mode: Mode;
	allDayAccessCheckboxSelection = false;
	canEditDeviceControls: boolean;
	isCreateDisabled = true;
	isEditDisabled = true;
	activeStatus = true;
	hasActiveStatus = false;

	wallMountTooltip = $localize`:@@deviceControlDetails-wallMountTooltip:Wall-mounted devices do not use hasp codes`;

	get isEdit(): boolean {
		return (
			this.mode === Mode.edit &&
			this.canEditDeviceControls &&
			!this.control.isUniversal
		);
	}

	get isCreate(): boolean {
		return this.mode === Mode.create && this.canEditDeviceControls;
	}

	get isViewOnly(): boolean {
		return (
			this.mode === Mode.viewOnly ||
			!this.canEditDeviceControls ||
			this.control.isUniversal
		);
	}

	get controlTitle(): string {
		switch (this.mode) {
			case Mode.create:
				return 'Create Control';
			case Mode.edit:
				return 'Edit Control';
			case Mode.viewOnly:
				return 'Control';
		}
	}

	get showImpactWarning(): boolean {
		return (
			!!this.control.assignedDeviceGroupCount ||
			!!this.control.assignedEntityCount
		);
	}

	is247CheckboxDisabled(): boolean {
		// Check if any days are disabled
		const anyDayDisabled =
			(this.edits.days.sunday.disabled ?? false) ||
			(this.edits.days.monday.disabled ?? false) ||
			(this.edits.days.tuesday.disabled ?? false) ||
			(this.edits.days.wednesday.disabled ?? false) ||
			(this.edits.days.thursday.disabled ?? false) ||
			(this.edits.days.friday.disabled ?? false) ||
			(this.edits.days.saturday.disabled ?? false);

		// Check if inherited time range allows 24/7
		const inheritedTimeRangeAllows247 =
			this.minStartTime === '00:00:00' &&
			(this.maxEndTime === '24:00:00' || this.maxEndTime === '00:00:00');

		// Disable if:
		// - Any day is disabled OR
		// - Current times aren't 24/7 OR
		// - Inherited time range doesn't allow 24/7 OR
		// - Not a new control (has ID)
		return (
			(anyDayDisabled || !inheritedTimeRangeAllows247) && this.edits.id !== ''
		);
	}

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: {
			deviceControlId?: string;
			control?: DeviceControl;
			deviceAssignments?: DeviceAssignments;
			viewMode: Mode;
		},
		private readonly dialogRef: MatDialogRef<DeviceControlDetailsCommonComponent>,
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		private readonly snackbar: MatSnackBar,
		private readonly scService: SystemCapabilityService
	) {
		this.canEditDeviceControls = scService.has(
			SystemCapabilityEnum.deviceManagement
		);
		this.deviceControlId = data?.deviceControlId ?? '';
		this.control = data?.control ?? emptyDeviceControl();
		this.edits = { ...this.control };
		this.deviceAssignments = data?.deviceAssignments ?? emptyDeviceAssignment();
		this.deviceAssignmentEdits = { ...this.deviceAssignments };
		this.edits.days = { ...this.control.days };
		this.edits.permissions = { ...this.control.permissions };
		this.mode = data.viewMode;
		this.activeStatus = data.control?.active ?? true;
		this.hasActiveStatus = data.viewMode !== Mode.create;
	}

	onCloseClicked(): void {
		let control;
		switch (this.mode) {
			case Mode.create:
				control = emptyDeviceControl();
				break;
			case Mode.edit:
				control = this.control;
				break;
		}

		if (!!control && JSON.stringify(control) !== JSON.stringify(this.edits)) {
			this.dialog.open(CancelConfirmationComponent, {
				data: {
					dialogHeader: 'Exit without saving?',
					dialogContent:
						'Leaving this window will discard all changes. Do you want to proceed?',
					dialogPrimaryButtonText: 'Exit',
					dialogSecondaryButtonText: 'Continue',
					primaryFunction: () => {
						this.dialogRef.close();
					},
				},
			});
		} else {
			this.dialogRef.close();
		}
	}

	onEditClicked(): void {
		this.mode = Mode.edit;
	}

	onAllDayAccessClicked(event: any): void {
		this.allDayAccessCheckboxSelection = event.checked;
		if (this.allDayAccessCheckboxSelection) {
			this.edits.days = {
				sunday: { value: true },
				monday: { value: true },
				tuesday: { value: true },
				wednesday: { value: true },
				thursday: { value: true },
				friday: { value: true },
				saturday: { value: true },
			};
			this.edits.startTime = '00:00:00';
			this.edits.endTime = '24:00:00';
			this.setDeviceAccessPermissions(true);
		}
		this.saveControlChanges();
	}

	onDayOfWeekToggled(day: string, value: any): void {
		this.allDayAccessCheckboxSelection = false;
		switch (day) {
			case 'sunday':
				this.edits.days.sunday.value = value;
				break;
			case 'monday':
				this.edits.days.monday.value = value;
				break;
			case 'tuesday':
				this.edits.days.tuesday.value = value;
				break;
			case 'wednesday':
				this.edits.days.wednesday.value = value;
				break;
			case 'thursday':
				this.edits.days.thursday.value = value;
				break;
			case 'friday':
				this.edits.days.friday.value = value;
				break;
			case 'saturday':
				this.edits.days.saturday.value = value;
				break;
		}
		this.saveControlChanges();
	}

	onDeviceAccessToggled(checked: boolean): void {
		this.setDeviceAccessPermissions(checked);
		if (!checked) {
			this.clearAjaxCapabilities();
		}
		this.saveControlChanges();
	}

	onDeviceCapabilitiesToggled(deviceCapability: string, value: any): void {
		switch (deviceCapability) {
			case 'canUpdateFirmware':
				this.edits.permissions.canUpdateFirmware.value = value;
				break;
			case 'canInstallDevice':
				this.edits.permissions.canInstallDevice.value = value;
				break;
			case 'canRemoveDevice':
				this.edits.permissions.canRemoveDevice.value = value;
				break;
			case 'canArmDisarmAjaxAlarm':
				this.edits.permissions.canArmDisarmAjaxAlarm.value = value;
				if (!value) {
					this.edits.permissions.canOverrideAjaxAlarm.value = false;
				}
				break;
			case 'canOverrideAjaxAlarm':
				this.edits.permissions.canOverrideAjaxAlarm.value = value;
				break;
		}
		this.saveControlChanges();
	}

	private setDeviceAccessPermissions(checked: boolean): void {
		if (!this.edits.permissions.canOpenKeyDoor.disabled) {
			this.edits.permissions.canOpenKeyDoor.value = checked;
		}
		if (!this.edits.permissions.canReleaseHasp.disabled) {
			this.edits.permissions.canReleaseHasp.value = checked;
		}
	}

	private clearAjaxCapabilities(): void {
		this.edits.permissions.canArmDisarmAjaxAlarm.value = false;
		this.edits.permissions.canOverrideAjaxAlarm.value = false;
	}

	onDeviceAssignmentsToggled(deviceAssignment: string, value: any): void {
		switch (deviceAssignment) {
			case 'allowCodeGeneration':
				this.deviceAssignmentEdits.allowCodeGeneration.value = value;
				break;
			case 'allowHaspCodeGeneration':
				this.deviceAssignmentEdits.allowHaspCodeGeneration.value = value;
				break;
		}
	}

	onStartTimeSelected(startTime: string): void {
		this.allDayAccessCheckboxSelection = false;
		this.edits.startTime = startTime;
		// Reset end time only if it's before the new start time:
		// added isOwner as to keep the original functionality of start and end time.
		// For inherited control, the end time military picker needs to be disabled after the inherited end time and cannot be set to 00:00
		if (this.edits.endTime && this.edits.endTime <= startTime) {
			if (this.edits.isOwner) {
				this.edits.endTime = '00:00:00';
			} else {
				this.edits.endTime = this.getEffectiveMinTime();
			}
		}
		this.saveControlChanges();
	}

	onEndTimeSelected(endTime: string): void {
		this.allDayAccessCheckboxSelection = false;
		this.edits.endTime = endTime;
		this.saveControlChanges();
	}

	saveControlChanges(): void {
		// filter for true values only
		const permissionsCheck =
			Object.values(this.edits.permissions).filter((it) => it).length > 0;
		const daysCheck =
			Object.values(this.edits.days).filter((it) => it).length > 0;

		if (
			this.canEditDeviceControls &&
			(this.mode === Mode.create || this.mode === Mode.edit)
		) {
			switch (this.mode) {
				case Mode.create:
					this.isCreateDisabled = !(
						this.edits.name !== '' &&
						this.edits.startTime !== '' &&
						this.edits.endTime !== '' &&
						permissionsCheck &&
						daysCheck
					);
					break;

				case Mode.edit:
					this.isEditDisabled =
						this.edits === this.control || !permissionsCheck || !daysCheck;
					break;
			}
		}
	}

	editControl(): void {
		this.apiService
			.editDeviceControl(this.deviceControlId, this.edits)
			.then((_) => {
				this.snackbar.open(
					$localize`:@@deviceControlDetails-controlChangesSaved:Successfully saved Device Control changes.`,
					undefined,
					{
						duration: 2000,
					}
				);
				this.dialogRef.close();
			})
			.catch((_) => {
				this.snackbar.open(
					$localize`:@@deviceControlDetails-controlChangesError:There was an error while saving changes to the Device Control. Please try again.`,
					undefined,
					{
						duration: 2000,
					}
				);
			});
	}

	handleActiveStatus(): void {
		this.edits.active = !this.edits.active;
		this.editControl();
	}
}
